import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../../lib/firebase";
import NewPatientModal from "../../components/patient/NewPatientModal";
import {
  formatDateForDisplay,
  formatPhoneForDisplay,
  getPatientAddressDisplay,
  getPatientSearchText,
  sortPatients,
} from "../../utils/patient/patientUtils";

const pageStyle = {
  padding: 20,
  width: "100%",
  maxWidth: "100%",
  overflowX: "hidden",
  boxSizing: "border-box",
};

const panelStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  background: "#fff",
  padding: 16,
  boxShadow: "0 10px 25px rgba(15, 23, 42, 0.04)",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  background: "#fff",
  boxSizing: "border-box",
};

const buttonStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 14px",
  background: "#fff",
  cursor: "pointer",
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "#0f172a",
  color: "#fff",
  borderColor: "#0f172a",
};

function StatusPill({ patient }) {
  const hasOpenStatus = Number(patient.encounterCount || 0) > 0;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: hasOpenStatus ? "#dcfce7" : "#f1f5f9",
        color: hasOpenStatus ? "#166534" : "#475569",
      }}
    >
      {hasOpenStatus ? "Has encounters" : "No encounters"}
    </span>
  );
}

export default function PatientListPage() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showNewPatientModal, setShowNewPatientModal] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "patients"),
      (snapshot) => {
        const next = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort(sortPatients);

        setPatients(next);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading patients:", error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const filteredPatients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return patients;

    const parts = q.split(/\s+/).filter(Boolean);

    return patients.filter((patient) => {
      const haystack = getPatientSearchText(patient);
      return parts.every((part) => haystack.includes(part));
    });
  }, [patients, search]);

  function openPatient(patient, options = {}) {
    navigate(`/field-access/hq/patients/${patient.id}`, {
      state: {
        createEncounter: Boolean(options.createEncounter),
      },
    });
  }

  return (
    <div className="patient-page-shell" style={pageStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 16,
          alignItems: "flex-start",
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <div style={{ fontSize: 30, fontWeight: 800 }}>Patients</div>
          <div style={{ color: "#64748b", marginTop: 4 }}>
            Search patients, avoid duplicates, and open an existing chart before starting a new one.
          </div>
        </div>

        <button type="button" style={primaryButtonStyle} onClick={() => setShowNewPatientModal(true)}>
          + New Patient
        </button>
      </div>

      <div
        style={{
          ...panelStyle,
          marginBottom: 16,
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div
          style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 12,
          alignItems: "center",
        }}
        >
          <input
            style={inputStyle}
            placeholder="Search name, phone, DOB, email, address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div style={{ color: "#64748b", fontSize: 14 }}>
            {loading ? "Loading patients..." : `${filteredPatients.length} patient(s)`}
          </div>
        </div>
      </div>

      <div style={panelStyle}>
        {loading ? (
          <div style={{ color: "#64748b" }}>Loading patients...</div>
        ) : !filteredPatients.length ? (
          <div style={{ color: "#64748b" }}>No patients found.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {filteredPatients.map((patient) => (
              <div
                key={patient.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 16,
                  padding: 14,
                 display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                  alignItems: "start",
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  overflow: "hidden",
                  background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>
                    {patient.fullName || "Unnamed patient"}
                  </div>

                  <div style={{ color: "#64748b", fontSize: 14, marginTop: 4 }}>
                    DOB: {patient.dob || "—"} · Age: {patient.age || "—"} · {patient.gender || "—"}
                  </div>

                  <div style={{ color: "#64748b", fontSize: 14, marginTop: 4 }}>
                    Phone: {formatPhoneForDisplay(patient.phone) || patient.phone || "—"}
                  </div>

                  <div style={{ color: "#64748b", fontSize: 14, marginTop: 4 }}>
                    Address: {getPatientAddressDisplay(patient)}
                  </div>

                  <div style={{ color: "#64748b", fontSize: 14, marginTop: 4 }}>
                    Last seen: {formatDateForDisplay(patient.lastEncounterAt || patient.updatedAt)}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Status</div>
                  <StatusPill patient={patient} />
                </div>

                <div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Encounters</div>
                  <div style={{ fontWeight: 700 }}>{patient.encounterCount || 0}</div>
                </div>

               <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  justifyContent: "flex-start",
                  width: "100%",
                }}
              >
                  <button type="button" style={buttonStyle} onClick={() => openPatient(patient)}>
                    Open Chart
                  </button>
                  <button
                    type="button"
                    style={buttonStyle}
                    onClick={() => openPatient(patient, { createEncounter: true })}
                  >
                    New Encounter
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <NewPatientModal
        isOpen={showNewPatientModal}
        patients={patients}
        onClose={() => setShowNewPatientModal(false)}
        onPatientCreated={(patient) => openPatient(patient, { createEncounter: true })}
        onOpenExistingPatient={(patient, options) => openPatient(patient, options)}
      />
    </div>
  );
}